"use client";

import { useMemo, useState } from "react";
import { Link } from "@/i18n/navigation";
import Button from "@/components/ui/Button/Button";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    Avancer,
    Curseur,
    Ecran,
    Liste,
    Montant,
    Question,
    Recommencer,
    Resultat,
    euros,
    remplir,
    useParcours,
} from "./Journey";
import { coutPourNet } from "@/lib/tools/monthlyPayout";
import { SEUIL_DIVIDENDES_EURL } from "@/lib/tools/salaryDividends";
import { APPORT_CESSION, integrationFiscale, reinvestir, revendre, sortirDeLaHolding } from "@/lib/tools/holding";
import { expliquerHoldingDividendes, expliquerHoldingVente } from "@/lib/tools/explanations";
import { IndiceExplication, MontantExplicable, PanneauExplication, useExplications } from "./Explanation";
import { useDossierOutil, useSignalerDossier } from "./ToolsDossier";
import { reprendre } from "@/lib/tools/dossier";

/**
 * Une holding au-dessus de votre société : ce qu'elle vous ferait gagner.
 *
 * LA QUESTION N'A PAS UNE RÉPONSE, ELLE EN A TROIS. Tout le monde en parle
 * comme d'une astuce fiscale, et elle n'en est une que pour l'argent qui ne
 * vous est pas versé. Le parcours demande donc d'abord ce que le lecteur
 * veut faire de son argent : en vivre, le réinvestir, ou vendre un jour. Et
 * il répond chiffres à l'appui, y compris quand la réponse est non.
 *
 * DEUX COLONNES, SANS ET AVEC. Le résultat montre ce que traverse le même
 * euro dans les deux cas, puis ce qu'il devient le jour où le lecteur le
 * veut pour lui : la holding fait payer plus tard, et l'écran le dit.
 *
 * EN EURL, UNE FOURCHETTE PLUTÔT QU'UN CHIFFRE. Un arrêt de 2023 peut faire
 * cotiser les dividendes remontés à la holding : l'écran donne le plancher
 * et le plafond, et explique l'écart (voir lib/tools/holding.js).
 *
 * TOUT RESTE DANS LE NAVIGATEUR : aucun appel réseau, aucun stockage.
 */

const PHASES = {
    forme: 0,
    micro: 0,
    impotRevenu: 0,
    objectif: 1,
    existe: 1,
    benefice: 2,
    activite: 2,
    cipav: 2,
    remuneration: 2,
    capital: 2,
    acquisition: 2,
    valeurApport: 2,
    prix: 2,
    delai: 2,
    resultat: 3,
};

/** Sous mille euros, l'écart ne justifie pas une seconde société. */
const ECART_NEGLIGEABLE = 1000;

const L = {
    fr: {
        phases: ["Société", "Projet", "Chiffres"],
        retour: "Retour",
        forme: {
            titre: "Quelle société la holding détiendrait-elle ?",
            aide: "Si vous ne l'avez pas encore créée, choisissez la forme que vous visez.",
            choix: [
                {
                    cle: "sasu",
                    icone: "user-id-linear",
                    titre: "Une SASU",
                    detail: "Vous en êtes le président et le seul associé.",
                },
                {
                    cle: "eurl",
                    icone: "buildings-2-linear",
                    titre: "Une EURL à l'impôt sur les sociétés",
                    detail: "Vous en êtes le gérant et le seul associé.",
                },
                {
                    cle: "eurlIr",
                    icone: "document-text-linear",
                    titre: "Une EURL à l'impôt sur le revenu",
                    detail: "Vous n'avez jamais choisi l'impôt sur les sociétés.",
                },
                {
                    cle: "micro",
                    icone: "user-linear",
                    titre: "Une micro-entreprise",
                    detail: "Ou une autre entreprise individuelle, sans société.",
                },
            ],
        },
        micro: {
            titre: "Une holding a besoin d'une société au-dessous d'elle",
            aide: "Elle détient des parts de société. En micro-entreprise, il n'y en a pas : votre entreprise et vous ne faites qu'un, et rien ne peut remonter à une holding.",
            points: [
                "La question se posera le jour où vous passerez en EURL ou en SASU.",
                "Pour savoir laquelle des deux vous irait, comparez-les d'abord.",
            ],
            lien: "Comparer l'EURL et la SASU",
        },
        impotRevenu: {
            titre: "Sous une holding, votre EURL passerait à l'impôt sur les sociétés",
            aide: "Dès qu'une société détient vos parts, votre EURL est soumise à l'impôt sur les sociétés, sans option à prendre : son bénéfice ne serait plus imposé chez vous.",
            points: [
                "Ce changement décide de ce que vous vous versez, et sous quelle forme.",
                "Regardez d'abord ce que l'impôt sur les sociétés changerait pour vous.",
            ],
            lien: "Voir ce que l'IS changerait",
        },
        objectif: {
            titre: "Que voulez-vous faire de l'argent de votre société ?",
            aide: "Une holding ne fait pas le même effet selon ce que devient l'argent.",
            choix: [
                {
                    cle: "vivre",
                    icone: "wallet-money-linear",
                    titre: "Me le verser pour en vivre",
                    detail: "Vous vous versez vos bénéfices en dividendes, chaque année.",
                },
                {
                    cle: "reinvestir",
                    icone: "graph-up-linear",
                    titre: "Le réinvestir",
                    detail: "Racheter une autre société, lancer un projet, investir, sans passer par votre compte personnel.",
                },
                {
                    cle: "revendre",
                    icone: "hand-money-linear",
                    titre: "Revendre ma société un jour",
                    detail: "Vous voulez savoir ce que la vente vous coûterait en impôt, avec et sans holding.",
                },
            ],
        },
        benefice: {
            titre: "Combien votre société gagne-t-elle après vous avoir payé ?",
            aide: "Son bénéfice de l'année, après votre rémunération et vos cotisations, avant l'impôt sur les sociétés. C'est cette somme qui partirait en dividendes.",
            libelle: "Bénéfice avant impôt sur les sociétés",
            suivant: "Continuer",
        },
        activite: {
            titre: "Quelle est votre activité ?",
            aide: "Elle change une partie de vos cotisations d'indépendant, et donc ce que vos dividendes vous coûtent.",
            choix: [
                {
                    cle: "services",
                    icone: "shop-linear",
                    titre: "Commerce ou prestations",
                    detail: "Vous vendez, ou vous rendez des services à des entreprises ou à des particuliers.",
                },
                {
                    cle: "artisan",
                    icone: "settings-minimalistic-linear",
                    titre: "Artisanat",
                    detail: "Vous êtes immatriculé comme artisan.",
                },
                {
                    cle: "bnc",
                    icone: "pen-new-square-linear",
                    titre: "Activité libérale",
                    detail: "Conseil, développement, design, formation, rédaction.",
                },
                {
                    cle: "cipav",
                    icone: "stethoscope-linear",
                    titre: "Profession réglementée",
                    detail: "Architecte, psychologue, ostéopathe : vous cotisez à la Cipav.",
                },
            ],
        },
        cipav: {
            titre: "Pour la Cipav, nous n'avons pas encore le calcul",
            aide: "À la Cipav, vos cotisations de retraite suivent un barème à part, que nous ne savons pas encore calculer au centime. Plutôt qu'un chiffre faux, nous préférons ne rien vous afficher.",
            points: [
                "Soyez d'autant plus prudent : c'est pour un professionnel libéral seul dans sa société que la Cour de cassation a fait cotiser, en 2023, les dividendes versés à sa holding.",
                "Votre expert-comptable peut faire le calcul avec les taux de la Cipav.",
            ],
        },
        remuneration: {
            titre: "Combien vous versez-vous en rémunération sur l'année ?",
            aide: "Votre rémunération nette, avant impôt sur le revenu. Les cotisations de vos dividendes s'ajoutent à celles que vous payez déjà : sans elle, nous ne saurions pas les calculer.",
            libelle: "Rémunération nette sur l'année",
            aucune: "Vous ne vous versez pas de rémunération",
            suivant: "Continuer",
        },
        capital: {
            titre: "Combien avez-vous mis dans votre société ?",
            aide: "Comptez le capital, les primes d'émission, et l'argent que vous avez laissé sur votre compte courant d'associé. Au-delà de {seuil} de ce montant, vos dividendes paient des cotisations, comme un salaire.",
            libelle: "Capital, primes et compte courant",
            seuil: "Vos dividendes paieront des cotisations au-delà de {montant}.",
            suivant: "Voir ce que la holding changerait",
        },
        existe: {
            titre: "Votre société existe-t-elle déjà ?",
            aide: "Une holding créée avant votre société porte toute la plus-value. Créée après, elle ne porte que ce que votre société gagnera de valeur ensuite.",
            choix: [
                {
                    cle: "deja",
                    icone: "buildings-linear",
                    titre: "Oui, elle tourne déjà",
                    detail: "Vous apporteriez vos parts à une holding créée pour l'occasion.",
                },
                {
                    cle: "creer",
                    icone: "add-circle-linear",
                    titre: "Non, je vais la créer",
                    detail: "La holding détiendrait votre société dès sa création.",
                },
            ],
        },
        acquisition: {
            titreDeja: "Combien vos parts vous ont-elles coûté ?",
            titreCreer: "Combien allez-vous mettre au capital ?",
            aideDeja: "Le plus souvent, c'est le capital que vous avez apporté à la création. Votre plus-value sera l'écart entre ce montant et le prix de vente.",
            aideCreer: "C'est ce que la holding paiera pour les parts de votre société. Sa plus-value sera l'écart entre ce montant et le prix de vente.",
            libelleDeja: "Prix payé pour vos parts",
            libelleCreer: "Capital de votre société",
            suivant: "Continuer",
        },
        valeurApport: {
            titre: "Combien votre société vaut-elle aujourd'hui ?",
            aide: "C'est la valeur à laquelle vous apporteriez vos parts à la holding. La plus-value acquise jusque-là reste la vôtre, et son impôt est reporté : la holding ne porte que ce que votre société gagnera de valeur ensuite.",
            libelle: "Valeur de vos parts aujourd'hui",
            suivant: "Continuer",
        },
        prix: {
            titre: "À quel prix pensez-vous la vendre ?",
            aide: "Le prix que vous espérez obtenir pour toutes vos parts. Un ordre de grandeur suffit pour comparer la vente avec et sans holding.",
            libelle: "Prix de vente",
            suivant: "Continuer",
        },
        delai: {
            titreDeja: "Combien de temps après l'apport pensez-vous vendre ?",
            titreCreer: "Dans combien de temps pensez-vous vendre ?",
            aideDeja: "Avant deux ans, la plus-value de la holding n'a pas droit au taux réduit. Avant trois ans, elle doit réinvestir une partie du prix.",
            aideCreer: "Avant deux ans de détention, la plus-value de la holding n'a pas droit au taux réduit.",
            choixDeja: [
                {
                    cle: "moins2",
                    icone: "hourglass-linear",
                    titre: "Moins de deux ans après l'apport",
                    detail: "Vous avez déjà un acheteur en vue.",
                },
                {
                    cle: "deuxTrois",
                    icone: "clock-circle-linear",
                    titre: "Entre deux et trois ans",
                    detail: "La vente se prépare, sans date arrêtée.",
                },
                {
                    cle: "plus3",
                    icone: "calendar-linear",
                    titre: "Trois ans ou plus",
                    detail: "C'est un projet pour plus tard.",
                },
            ],
            choixCreer: [
                {
                    cle: "moins2",
                    icone: "hourglass-linear",
                    titre: "Moins de deux ans après la création",
                    detail: "Vous montez la société pour la revendre vite.",
                },
                {
                    cle: "plus3",
                    icone: "calendar-linear",
                    titre: "Deux ans ou plus",
                    detail: "C'est un projet pour plus tard.",
                },
            ],
        },
        resultat: {
            titre: "Votre holding",
            vivre: {
                perte: "Pour vous verser cet argent, la holding vous coûte {ecart} de plus",
                fourchette: "Pour vous verser cet argent, la holding peut vous faire gagner {gain}, ou perdre {perte}",
                detail: "Versés directement, vos dividendes vous laissent {sans}. Passés par la holding, qui vous les reverse ensuite, ils vous laissent {via} : les 31,4 % se paient quand même, et la holding ajoute son propre impôt. Sans compter ce que coûte sa comptabilité.",
                detailEurl:
                    "Lue à la lettre, la loi ne fait pas cotiser les dividendes versés à une holding : il vous resterait {via}. Mais si l'Urssaf suit l'arrêt de 2023 de la Cour de cassation, vous devez quand même les {cotisations} de cotisations, et il ne vous reste que {viaPrudent}. Et l'Urssaf peut écarter une holding qui ne sert qu'à éviter des cotisations, avec une pénalité de 20 %.",
            },
            reinvestir: {
                gain: "La holding garde {gain} de plus que vous pour réinvestir",
                fourchette: "La holding garde entre {prudent} et {gain} de plus que vous pour réinvestir",
                rien: "La holding ne garde presque rien de plus que vous",
                detail: "Versés à vous, vos {dividendes} de dividendes paient 31,4 % d'impôt et de prélèvements sociaux. Versés à la holding, ils ne paient l'impôt que sur 5 % de leur montant.",
                detailEurl:
                    "Versés à vous, vos {dividendes} de dividendes paient 12,8 % d'impôt, puis vos cotisations d'indépendant sur ce qui dépasse 10 % de votre capital. Versés à la holding, ils ne paient l'impôt que sur 5 % de leur montant. Le premier chiffre suppose que l'Urssaf vous les fait payer malgré la holding, comme la Cour de cassation l'a jugé en 2023 pour un praticien seul dans sa société. Le second suit la lettre de la loi, qui ne vise que les dividendes que vous touchez vous-même.",
                integrationTitre: "Et si les deux sociétés forment un groupe ?",
                integration:
                    "Si la holding détient au moins 95 % de votre société et choisit l'intégration fiscale, pour cinq exercices au moins, les deux sociétés ne paient plus qu'un impôt, sur leurs résultats additionnés, avec un seul plafond de 42 500 € au taux de 15 %. À partir du deuxième exercice, l'impôt n'y porte plus que sur 1 % des dividendes : il baisse ici de {economie} par an.",
            },
            revendre: {
                economie: "Vendue par une holding, votre société vous coûte {economie} d'impôt en moins",
                economieReport: "Vous payez {economie} d'impôt en moins, et {report} plus tard",
                report: "La holding repousse {report} d'impôt, sans presque rien vous faire économiser",
                rien: "La holding ne vous fait presque rien économiser",
                detailSans: "Vendue par vous, votre société vous coûterait {sans} d'impôt sur une plus-value de {plusValue}.",
                detailDouze: "Vendue par la holding au moins deux ans après son entrée, sa plus-value ne paie l'impôt que sur 12 % de son montant : {avec}.",
                detailCourt: "Vendue par la holding moins de deux ans après son entrée, sa plus-value paie l'impôt sur les sociétés en entier : {avec}.",
                detailZero: "Vendue par la holding au prix de l'apport, elle ne dégage aucune plus-value : la holding ne paie rien.",
            },
            colonnes: {
                sans: "Sans holding",
                avec: "Avec une holding",
                dividendes: "Dividendes",
                impotRevenu: "Impôt sur le revenu, 12,8 %",
                prelevements: "Prélèvements sociaux, 18,6 %",
                prelevementsSur: "Prélèvements sociaux, 18,6 % de {base}",
                cotisations: "Cotisations d'indépendant sur {base}",
                impotHolding: "Impôt de la holding, sur 5 %",
                reverse: "31,4 % quand elle vous les reverse",
                surCompte: "Arrive sur votre compte",
                dansHolding: "Reste dans la holding",
                prix: "Prix de vente",
                impotVente: "Impôt sur la plus-value, 31,4 %",
                impotHoldingVente: "Impôt de la holding, sur 12 %",
                impotHoldingCourt: "Impôt sur les sociétés",
                prudent: "Si l'Urssaf suit l'arrêt de 2023, ajoutez {cotisations} de cotisations à votre charge.",
            },
            sortie: {
                titreReinvestir: "Et le jour où vous voudrez cet argent ?",
                titreRevendre: "Et si vous voulez le prix pour vous ?",
                texte: "La holding vous le versera en dividendes, et les 31,4 % se paieront à ce moment-là : il vous resterait {via}, contre {sans} en direct. Elle ne vous fait donc gagner que sur l'argent qu'elle garde et réinvestit.",
                texteReport:
                    "La holding vous le versera en dividendes, et les 31,4 % se paieront à ce moment-là : il vous resterait {via}, contre {sans} en direct, et les {report} d'impôt reporté resteraient dus le jour où vous vendriez les parts de la holding. Elle ne vous fait donc gagner que sur l'argent qu'elle garde et réinvestit.",
                texteEurl:
                    "La holding vous le versera en dividendes, avec 31,4 % d'impôt et de prélèvements sociaux : il vous resterait {via}, contre {sans} en direct. C'est plus, parce que vos cotisations sautent, mais seulement si l'Urssaf s'en tient à la lettre de la loi. Si elle suit l'arrêt de 2023, il ne vous reste que {viaPrudent}.",
            },
            report: {
                titre: "Ce que votre société a déjà gagné reste à vous",
                texte: "Elle a déjà pris {plusValue} de valeur. En l'apportant à la holding, vous ne payez pas tout de suite les {impot} d'impôt dessus : ils sont reportés, et deviennent dus le jour où vous vendez les parts de la holding.",
            },
            remploi: {
                titre: "Si vous vendez dans les trois ans qui suivent l'apport",
                texte: "Pour garder le report, la holding doit réinvestir au moins 70 % du prix, soit {quota}, dans les trois ans, dans une activité économique, et garder ce qu'elle achète au moins cinq ans. Sinon, le report s'arrête et les {report} deviennent dus.",
                points: [
                    "Racheter une société et en prendre le contrôle, financer sa propre activité, entrer au capital d'une société qui en exerce une : oui.",
                    "Placements financiers, immobilier, location meublée : non.",
                ],
            },
            gerant: {
                titre: "En EURL, votre statut de gérant peut changer",
                texte: "Une fois vos parts dans la holding, vous n'êtes plus associé de votre EURL, seulement son gérant. La loi fait alors de vous un assimilé salarié si vous êtes rémunéré, ce qui coûte bien plus cher en cotisations. Mais les juges ne sont pas d'accord entre eux, et l'administration fiscale compte les parts de votre holding comme les vôtres. Posez la question par écrit à l'Urssaf avant de créer la holding. Sous une SASU, elle ne se pose pas : son président est assimilé salarié dans tous les cas.",
            },
            couts: {
                titre: "Ce que la holding vous coûte, et que ce calcul ne compte pas",
                items: [
                    "Une deuxième comptabilité, avec une annexe, un rapport de gestion et la liste de ses filiales, même si elle est toute petite.",
                    "Des comptes publics : la holding ne peut pas les garder confidentiels.",
                    "Une décision formelle chaque année pour approuver les comptes de votre société, qui ne peut plus se contenter de les déposer.",
                    "Sans doute la cotisation foncière des entreprises, au minimum, même sans local.",
                    "Si vous la créez en EURL, une option pour l'impôt sur les sociétés à prendre avant la fin de son troisième mois.",
                ],
            },
            protection: {
                titre: "Ce qui protège votre holding",
                texte: "Une holding sans autre raison d'être que de payer moins peut être écartée par l'administration fiscale, quand l'avantage fiscal est l'un de ses objectifs principaux et que le montage ne reflète pas la réalité économique. Un vrai projet la protège : réinvestir, racheter une autre société, préparer une vente.",
                texteEurl:
                    "Une holding sans autre raison d'être que de payer moins peut être écartée : par l'administration fiscale, quand l'avantage fiscal est l'un de ses objectifs principaux et que le montage ne reflète pas la réalité économique, et par l'Urssaf pour les cotisations, avec une pénalité de 20 %. Un vrai projet la protège : réinvestir, racheter une autre société, préparer une vente.",
            },
            hypotheses: {
                titre: "Pour ce calcul, nous avons supposé que :",
                dividendes: [
                    "tout le bénéfice qui reste après l'impôt sur les sociétés est distribué dans l'année ;",
                    "vos dividendes paient le prélèvement forfaitaire de 31,4 %, sans option pour le barème ;",
                    "la holding est une SASU, à l'impôt sur les sociétés au taux de 15 % sur ses premiers 42 500 € de bénéfice.",
                ],
                eurl: "votre rémunération reste la même, avec ou sans holding, et votre statut de gérant aussi ;",
                vente: [
                    "sans holding, vous payez le prélèvement forfaitaire de 31,4 %. Au-delà de 250 000 € de revenu dans l'année, 500 000 € en couple, la contribution sur les hauts revenus s'y ajoute : l'écart réel serait un peu plus grand ;",
                    "vos parts ont été créées ou achetées depuis 2018 : pour des parts plus anciennes, un abattement pour durée de détention peut réduire l'impôt au barème ;",
                    "vous ne partez pas à la retraite en vendant : un abattement de 500 000 € peut alors réduire l'impôt d'une vente faite en direct, et changer la réponse ;",
                    "la holding est une SASU, à l'impôt sur les sociétés au taux de 15 % sur ses premiers 42 500 € de bénéfice.",
                ],
            },
            avertissement:
                "Ce calcul vous donne un ordre de grandeur. Une holding engage votre société pour des années : faites valider le montage par votre expert-comptable ou un avocat avant de créer quoi que ce soit.",
            recommencer: "Refaire le calcul",
        },
    },
    en: {
        phases: ["Company", "Plan", "Figures"],
        retour: "Back",
        forme: {
            titre: "Which company would the holding own?",
            aide: "If you have not set it up yet, pick the form you are aiming for.",
            choix: [
                {
                    cle: "sasu",
                    icone: "user-id-linear",
                    titre: "A SASU",
                    detail: "You are its president and only shareholder.",
                },
                {
                    cle: "eurl",
                    icone: "buildings-2-linear",
                    titre: "An EURL under corporate tax",
                    detail: "You are its manager and only shareholder.",
                },
                {
                    cle: "eurlIr",
                    icone: "document-text-linear",
                    titre: "An EURL under income tax",
                    detail: "You have never chosen corporate tax.",
                },
                {
                    cle: "micro",
                    icone: "user-linear",
                    titre: "A micro-enterprise",
                    detail: "Or another sole proprietorship, with no company.",
                },
            ],
        },
        micro: {
            titre: "A holding needs a company beneath it",
            aide: "It owns shares in a company. In a micro-enterprise there are none: you and your business are one and the same, and nothing can flow up to a holding.",
            points: [
                "The question will arise the day you move to an EURL or a SASU.",
                "To know which of the two would suit you, compare them first.",
            ],
            lien: "Compare the EURL and the SASU",
        },
        impotRevenu: {
            titre: "Under a holding, your EURL would move to corporate tax",
            aide: "As soon as a company owns your shares, your EURL is subject to corporate tax, with no option to take: its profit would no longer be taxed in your hands.",
            points: [
                "That change decides what you pay yourself, and in what form.",
                "First look at what corporate tax would change for you.",
            ],
            lien: "See what corporate tax would change",
        },
        objectif: {
            titre: "What do you want to do with your company's money?",
            aide: "A holding does not have the same effect depending on what happens to the money.",
            choix: [
                {
                    cle: "vivre",
                    icone: "wallet-money-linear",
                    titre: "Pay it to myself to live on",
                    detail: "You pay yourself your profits as dividends, every year.",
                },
                {
                    cle: "reinvestir",
                    icone: "graph-up-linear",
                    titre: "Reinvest it",
                    detail: "Buy another company, launch a project, invest, without going through your personal account.",
                },
                {
                    cle: "revendre",
                    icone: "hand-money-linear",
                    titre: "Sell my company one day",
                    detail: "You want to know what the sale would cost you in tax, with and without a holding.",
                },
            ],
        },
        benefice: {
            titre: "How much does your company make after paying you?",
            aide: "Its profit for the year, after your pay and your contributions, before corporate tax. It is the amount that would go out as dividends.",
            libelle: "Profit before corporate tax",
            suivant: "Continue",
        },
        activite: {
            titre: "What is your activity?",
            aide: "It changes part of your self-employed contributions, and so what your dividends cost you.",
            choix: [
                {
                    cle: "services",
                    icone: "shop-linear",
                    titre: "Trade or services",
                    detail: "You sell goods, or provide services to businesses or individuals.",
                },
                {
                    cle: "artisan",
                    icone: "settings-minimalistic-linear",
                    titre: "Craft trade",
                    detail: "You are registered as a craftsperson.",
                },
                {
                    cle: "bnc",
                    icone: "pen-new-square-linear",
                    titre: "Liberal profession",
                    detail: "Consulting, development, design, training, writing.",
                },
                {
                    cle: "cipav",
                    icone: "stethoscope-linear",
                    titre: "Regulated profession",
                    detail: "Architect, psychologist, osteopath: you contribute to the Cipav.",
                },
            ],
        },
        cipav: {
            titre: "We do not have the Cipav calculation yet",
            aide: "With the Cipav, your pension contributions follow their own scale, which we cannot yet compute to the cent. Rather than show you a wrong figure, we prefer to show nothing.",
            points: [
                "Be all the more careful: it was for a liberal professional alone in his company that the Court of Cassation made the dividends paid to his holding carry contributions, in 2023.",
                "Your accountant can run the calculation with the Cipav rates.",
            ],
        },
        remuneration: {
            titre: "How much do you pay yourself over the year?",
            aide: "Your net pay, before income tax. The contributions on your dividends come on top of those you already pay: without it, we could not calculate them.",
            libelle: "Net pay over the year",
            aucune: "You pay yourself no salary",
            suivant: "Continue",
        },
        capital: {
            titre: "How much have you put into your company?",
            aide: "Count the share capital, share premiums, and the money you have left in your shareholder current account. Beyond {seuil} of that amount, your dividends carry contributions, just like a salary.",
            libelle: "Capital, premiums and current account",
            seuil: "Your dividends will carry contributions beyond {montant}.",
            suivant: "See what a holding would change",
        },
        existe: {
            titre: "Does your company already exist?",
            aide: "A holding set up before your company carries the whole capital gain. Set up afterwards, it only carries the value your company gains from then on.",
            choix: [
                {
                    cle: "deja",
                    icone: "buildings-linear",
                    titre: "Yes, it is already running",
                    detail: "You would contribute your shares to a holding set up for the purpose.",
                },
                {
                    cle: "creer",
                    icone: "add-circle-linear",
                    titre: "No, I am going to set it up",
                    detail: "The holding would own your company from day one.",
                },
            ],
        },
        acquisition: {
            titreDeja: "What did your shares cost you?",
            titreCreer: "How much share capital will you put in?",
            aideDeja: "Most often, it is the capital you put in when you set it up. Your capital gain will be the difference between that amount and the sale price.",
            aideCreer: "It is what the holding will pay for your company's shares. Its capital gain will be the difference between that amount and the sale price.",
            libelleDeja: "Price paid for your shares",
            libelleCreer: "Your company's share capital",
            suivant: "Continue",
        },
        valeurApport: {
            titre: "What is your company worth today?",
            aide: "It is the value at which you would contribute your shares to the holding. The gain built up so far stays yours, and its tax is deferred: the holding only carries the value your company gains from then on.",
            libelle: "Value of your shares today",
            suivant: "Continue",
        },
        prix: {
            titre: "What price do you expect to sell it for?",
            aide: "The price you hope to get for all your shares. A rough figure is enough to compare the sale with and without a holding.",
            libelle: "Sale price",
            suivant: "Continue",
        },
        delai: {
            titreDeja: "How long after the contribution do you expect to sell?",
            titreCreer: "How soon do you expect to sell?",
            aideDeja: "Before two years, the holding's capital gain does not qualify for the reduced rate. Before three years, it must reinvest part of the price.",
            aideCreer: "Before two years of ownership, the holding's capital gain does not qualify for the reduced rate.",
            choixDeja: [
                {
                    cle: "moins2",
                    icone: "hourglass-linear",
                    titre: "Less than two years after the contribution",
                    detail: "You already have a buyer in view.",
                },
                {
                    cle: "deuxTrois",
                    icone: "clock-circle-linear",
                    titre: "Between two and three years",
                    detail: "The sale is in preparation, with no set date.",
                },
                {
                    cle: "plus3",
                    icone: "calendar-linear",
                    titre: "Three years or more",
                    detail: "It is a plan for later.",
                },
            ],
            choixCreer: [
                {
                    cle: "moins2",
                    icone: "hourglass-linear",
                    titre: "Less than two years after setting it up",
                    detail: "You are building the company to sell it quickly.",
                },
                {
                    cle: "plus3",
                    icone: "calendar-linear",
                    titre: "Two years or more",
                    detail: "It is a plan for later.",
                },
            ],
        },
        resultat: {
            titre: "Your holding",
            vivre: {
                perte: "To pay yourself this money, the holding costs you {ecart} more",
                fourchette: "To pay yourself this money, the holding may earn you {gain}, or cost you {perte}",
                detail: "Paid to you directly, your dividends leave you {sans}. Routed through the holding, which then pays them on to you, they leave you {via}: the 31.4% is paid all the same, and the holding adds its own tax. Not counting what its accounts cost.",
                detailEurl:
                    "Read to the letter, the law does not make dividends paid to a holding carry contributions: you would keep {via}. But if the Urssaf follows the Court of Cassation's 2023 ruling, you still owe the {cotisations} of contributions, and you only keep {viaPrudent}. And the Urssaf can set aside a holding whose only purpose is to avoid contributions, with a 20% penalty.",
            },
            reinvestir: {
                gain: "The holding keeps {gain} more than you to reinvest",
                fourchette: "The holding keeps between {prudent} and {gain} more than you to reinvest",
                rien: "The holding keeps hardly anything more than you",
                detail: "Paid to you, your {dividendes} of dividends bear 31.4% of tax and social levies. Paid to the holding, they are only taxed on 5% of their amount.",
                detailEurl:
                    "Paid to you, your {dividendes} of dividends bear 12.8% of tax, then your self-employed contributions on whatever exceeds 10% of your capital. Paid to the holding, they are only taxed on 5% of their amount. The first figure assumes the Urssaf makes you pay them despite the holding, as the Court of Cassation ruled in 2023 for a practitioner alone in his company. The second follows the letter of the law, which only covers the dividends you receive yourself.",
                integrationTitre: "And if the two companies form a group?",
                integration:
                    "If the holding owns at least 95% of your company and opts for tax consolidation, for at least five financial years, the two companies pay a single tax, on their combined results, with a single €42,500 ceiling at the 15% rate. From the second financial year, only 1% of the dividends is taxed: here the tax falls by {economie} a year.",
            },
            revendre: {
                economie: "Sold by a holding, your company costs you {economie} less in tax",
                economieReport: "You pay {economie} less in tax, and {report} later",
                report: "The holding defers {report} of tax, while saving you almost nothing",
                rien: "The holding saves you almost nothing",
                detailSans: "Sold by you, your company would cost you {sans} of tax on a capital gain of {plusValue}.",
                detailDouze: "Sold by the holding at least two years after it came in, its capital gain is only taxed on 12% of its amount: {avec}.",
                detailCourt: "Sold by the holding less than two years after it came in, its whole capital gain bears corporate tax: {avec}.",
                detailZero: "Sold by the holding at the contribution value, it makes no capital gain: the holding pays nothing.",
            },
            colonnes: {
                sans: "Without a holding",
                avec: "With a holding",
                dividendes: "Dividends",
                impotRevenu: "Income tax, 12.8%",
                prelevements: "Social levies, 18.6%",
                prelevementsSur: "Social levies, 18.6% of {base}",
                cotisations: "Self-employed contributions on {base}",
                impotHolding: "Holding's tax, on 5%",
                reverse: "31.4% when it pays them on to you",
                surCompte: "Reaches your account",
                dansHolding: "Stays in the holding",
                prix: "Sale price",
                impotVente: "Tax on the capital gain, 31.4%",
                impotHoldingVente: "Holding's tax, on 12%",
                impotHoldingCourt: "Corporate tax",
                prudent: "If the Urssaf follows the 2023 ruling, add {cotisations} of contributions payable by you.",
            },
            sortie: {
                titreReinvestir: "And the day you want this money?",
                titreRevendre: "And if you want the price for yourself?",
                texte: "The holding will pay it to you as dividends, and the 31.4% will be paid then: you would keep {via}, against {sans} directly. It therefore only earns you something on the money it keeps and reinvests.",
                texteReport:
                    "The holding will pay it to you as dividends, and the 31.4% will be paid then: you would keep {via}, against {sans} directly, and the {report} of deferred tax would still fall due the day you sold the holding's shares. It therefore only earns you something on the money it keeps and reinvests.",
                texteEurl:
                    "The holding will pay it to you as dividends, with 31.4% of tax and social levies: you would keep {via}, against {sans} directly. That is more, because your contributions fall away, but only if the Urssaf sticks to the letter of the law. If it follows the 2023 ruling, you only keep {viaPrudent}.",
            },
            report: {
                titre: "What your company has already gained stays yours",
                texte: "It has already gained {plusValue} in value. By contributing it to the holding, you do not pay the {impot} of tax on it straight away: it is deferred, and falls due the day you sell the holding's shares.",
            },
            remploi: {
                titre: "If you sell within three years of the contribution",
                texte: "To keep the deferral, the holding must reinvest at least 70% of the price, that is {quota}, within three years, in an economic activity, and keep what it buys for at least five years. Otherwise, the deferral ends and the {report} fall due.",
                points: [
                    "Buying a company and taking control of it, funding its own activity, subscribing to the capital of a company that runs one: yes.",
                    "Financial investments, property, furnished lettings: no.",
                ],
            },
            gerant: {
                titre: "In an EURL, your status as manager may change",
                texte: "Once your shares are in the holding, you are no longer a shareholder of your EURL, only its manager. The law then treats you as an assimilated employee if you are paid, which costs far more in contributions. But the judges disagree with each other, and the tax authority counts your holding's shares as yours. Put the question to the Urssaf in writing before setting up the holding. Under a SASU, it does not arise: its president is an assimilated employee in every case.",
            },
            couts: {
                titre: "What the holding costs you, and this calculation does not count",
                items: [
                    "A second set of accounts, with notes, a management report and the list of its subsidiaries, however small it is.",
                    "Public accounts: the holding cannot keep them confidential.",
                    "A formal decision every year to approve your company's accounts, which can no longer simply file them.",
                    "Most likely the business property contribution (CFE), at the minimum, even without premises.",
                    "If you set it up as an EURL, an option for corporate tax to take before the end of its third month.",
                ],
            },
            protection: {
                titre: "What protects your holding",
                texte: "A holding with no reason to exist other than paying less can be set aside by the tax authority, when the tax advantage is one of its main aims and the arrangement does not reflect economic reality. A real project protects it: reinvesting, buying another company, preparing a sale.",
                texteEurl:
                    "A holding with no reason to exist other than paying less can be set aside: by the tax authority, when the tax advantage is one of its main aims and the arrangement does not reflect economic reality, and by the Urssaf for contributions, with a 20% penalty. A real project protects it: reinvesting, buying another company, preparing a sale.",
            },
            hypotheses: {
                titre: "For this calculation, we assumed that:",
                dividendes: [
                    "all the profit left after corporate tax is distributed within the year;",
                    "your dividends bear the 31.4% flat tax, without opting for the scale;",
                    "the holding is a SASU, under corporate tax at 15% on its first €42,500 of profit.",
                ],
                eurl: "your pay stays the same, with or without a holding, and so does your status as manager;",
                vente: [
                    "without a holding, you pay the 31.4% flat tax. Above €250,000 of income in the year, €500,000 as a couple, the high-income contribution comes on top: the real gap would be a little wider;",
                    "your shares were created or bought since 2018: for older shares, a holding-period allowance can reduce the tax on the scale;",
                    "you are not retiring when you sell: a €500,000 allowance can then reduce the tax on a direct sale, and change the answer;",
                    "the holding is a SASU, under corporate tax at 15% on its first €42,500 of profit.",
                ],
            },
            avertissement:
                "This calculation gives you an order of magnitude. A holding commits your company for years: have the arrangement checked by your accountant or a lawyer before setting anything up.",
            recommencer: "Start again",
        },
    },
};

/**
 * Une colonne du comparatif : ce que traverse le même argent, ligne par ligne.
 * Les retenues s'affichent précédées d'un moins, le total en pied.
 */
function Colonne({ titre, lignes, total, libelleTotal, explicationTotal, lang, reduit, accent = false, ouverte, basculer }) {
    const sommes = (v) => euros(Math.round(v), lang);
    const cleTotal = `${titre}:total`;
    return (
        // LES DEUX TOTAUX SUR LA MÊME LIGNE. Les colonnes n'ont pas le même
        // nombre de lignes : la grille les étire à la même hauteur, et le
        // total descend en pied de carte au lieu de laisser un blanc dessous.
        // overflow-hidden garde son fond dans les coins arrondis.
        <div
            className={`flex flex-col overflow-hidden rounded-xl border ${
                accent ? "border-primary/40" : "border-gray-200/70 dark:border-white/10"
            }`}
        >
            <div className="px-4 py-2.5 text-sm font-semibold dark:text-white border-b border-gray-200/70 dark:border-white/10">
                {titre}
            </div>
            <div className="flex-1 divide-y divide-gray-200/70 dark:divide-white/10">
                {lignes.map(([libelle, valeur, retenue, explication]) => {
                    // Chaque montant dit d'où il vient : il se déplie sous sa ligne.
                    const cle = `${titre}:${libelle}`;
                    return (
                        <div key={libelle} className="px-4 py-2.5 text-sm">
                            <div className="flex items-baseline justify-between gap-4">
                                <span className="text-gray-600 dark:text-lightBlue">{libelle}</span>
                                <span className="tabular-nums whitespace-nowrap dark:text-white">
                                    {retenue && valeur >= 0.5 ? "− " : ""}
                                    <MontantExplicable
                                        explication={explication}
                                        ouvert={ouverte === cle}
                                        onBasculer={() => basculer(cle)}
                                    >
                                        {sommes(valeur)}
                                    </MontantExplicable>
                                </span>
                            </div>
                            {ouverte === cle && <PanneauExplication explication={explication} lang={lang} />}
                        </div>
                    );
                })}
            </div>
            <div
                className={`px-4 py-3 border-t border-gray-200/70 dark:border-white/10 ${
                    accent ? "bg-primary/[0.06]" : "bg-gray-50 dark:bg-white/[0.04]"
                }`}
            >
                <div className="flex items-baseline justify-between gap-4">
                    <span className="font-semibold dark:text-white">{libelleTotal}</span>
                    <span className="font-bold tabular-nums whitespace-nowrap dark:text-white">
                        <MontantExplicable
                            explication={explicationTotal}
                            ouvert={ouverte === cleTotal}
                            onBasculer={() => basculer(cleTotal)}
                        >
                            <Montant valeur={Math.max(0, Math.round(total))} lang={lang} reduit={reduit} />
                        </MontantExplicable>
                    </span>
                </div>
                {ouverte === cleTotal && <PanneauExplication explication={explicationTotal} lang={lang} />}
            </div>
        </div>
    );
}

/** Un encadré titré, pour chaque point d'attention sous le comparatif. */
function Encadre({ icone, titre, children }) {
    return (
        <div className="mt-6 rounded-xl bg-gray-50 dark:bg-white/[0.04] px-4 py-4">
            <div className="flex items-center gap-2 font-semibold dark:text-white">
                <SolarIcon name={icone} className="w-5 h-5 text-primary shrink-0" />
                {titre}
            </div>
            {children}
        </div>
    );
}

export default function Holding({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const r = t.resultat;

    // Le dossier du lecteur, s'il en a un : les réponses qu'il connaît
    // partent déjà posées, et le parcours s'ouvre sur la première qui manque.
    // La micro, l'EURL à l'IR et la CIPAV ne sont jamais des valeurs de
    // l'outil : ce sont des écrans de refus, où la reprise mène directement.
    const { valeurs: dossier } = useDossierOutil();
    const formeDossier = dossier.forme === "eurl" || dossier.forme === "sasu" ? dossier.forme : undefined;
    const activiteDossier = dossier.activite !== "cipav" ? dossier.activite : undefined;
    // Un bénéfice sous le minimum du curseur ne se reprend pas : la question reste.
    const beneficeDossier = dossier.benefice >= 5000 ? dossier.benefice : undefined;
    const [forme, setForme] = useState(formeDossier ?? null);
    const [objectif, setObjectif] = useState(null);
    const [activite, setActivite] = useState(activiteDossier ?? null);
    const [benefice, setBenefice] = useState(beneficeDossier ?? 60000);
    const [remuneration, setRemuneration] = useState(36000);
    const [capital, setCapital] = useState(1000);
    const [existe, setExiste] = useState(null);
    const [acquisition, setAcquisition] = useState(1000);
    const [valeurApport, setValeurApport] = useState(100000);
    const [prix, setPrix] = useState(300000);
    const [delai, setDelai] = useState(null);

    const { etape, communs, rang, aller, retour, recommencer, reduit } = useParcours(
        "forme",
        () => {
            setForme(null);
            setObjectif(null);
            setActivite(null);
            setBenefice(60000);
            setRemuneration(36000);
            setCapital(1000);
            setExiste(null);
            setAcquisition(1000);
            setValeurApport(100000);
            setPrix(300000);
            setDelai(null);
        },
        PHASES,
        reprendre(
            dossier.forme === "micro"
                ? [{ etape: "forme", connu: true }, { etape: "micro" }]
                : dossier.forme === "eurlIr"
                  ? [{ etape: "forme", connu: true }, { etape: "impotRevenu" }]
                  : [
                        { etape: "forme", connu: formeDossier !== undefined },
                        // Le projet (vivre, réinvestir, revendre) ne se déduit pas
                        // du dossier : le parcours s'y arrête.
                        { etape: "objectif", connu: false },
                        { etape: "resultat" },
                    ],
        ),
    );

    useSignalerDossier({ forme, activite }, etape === "resultat");

    const calcul = useMemo(() => {
        if (etape !== "resultat") return null;
        if (objectif === "revendre") {
            return revendre({
                prix,
                acquisition,
                valeurApport: existe === "deja" ? valeurApport : null,
                deuxAns: delai !== "moins2",
            });
        }
        const metier = activite === "artisan" ? "services" : activite || "bnc";
        const artisan = activite === "artisan";
        // Les cotisations des dividendes s'ajoutent à celles de la
        // rémunération : il faut l'enveloppe que la rémunération nette coûte.
        const enveloppeRemuneration =
            forme === "eurl" ? coutPourNet({ forme: "eurl", net: remuneration, activite: metier, artisan }).cout : 0;
        return reinvestir({ forme, benefice, capital, enveloppeRemuneration, activite: metier, artisan });
    }, [etape, objectif, prix, acquisition, existe, valeurApport, delai, activite, forme, remuneration, benefice, capital]);

    // Chaque montant des deux colonnes dit d'où il vient : les mêmes moteurs,
    // écrits étape par étape (lib/tools/explanations.js).
    const x = useMemo(() => {
        if (etape !== "resultat") return null;
        if (objectif === "revendre") {
            return expliquerHoldingVente(
                { prix, acquisition, valeurApport: existe === "deja" ? valeurApport : null, deuxAns: delai !== "moins2" },
                lang,
            );
        }
        const metier = activite === "artisan" ? "services" : activite || "bnc";
        const artisan = activite === "artisan";
        const enveloppeRemuneration =
            forme === "eurl" ? coutPourNet({ forme: "eurl", net: remuneration, activite: metier, artisan }).cout : 0;
        return expliquerHoldingDividendes({ forme, benefice, capital, enveloppeRemuneration, activite: metier, artisan }, lang);
    }, [etape, objectif, prix, acquisition, existe, valeurApport, delai, activite, forme, remuneration, benefice, capital, lang]);
    const { ouverte, basculer } = useExplications();

    const communsEcran = { ...communs, phases: t.phases, retour: t.retour, onRetour: retour };
    const sommes = (v) => euros(Math.round(v), lang);

    if (etape === "forme") {
        return (
            <Question
                key="forme"
                {...communsEcran}
                phase={PHASES.forme}
                rang={rang}
                titre={t.forme.titre}
                aide={t.forme.aide}
                choix={t.forme.choix.map((c) => ({
                    icone: c.icone,
                    titre: c.titre,
                    detail: c.detail,
                    onClick: () => {
                        if (c.cle === "eurlIr") {
                            aller("impotRevenu");
                            return;
                        }
                        if (c.cle === "micro") {
                            aller("micro");
                            return;
                        }
                        setForme(c.cle);
                        aller("objectif");
                    },
                }))}
            />
        );
    }

    if (etape === "micro" || etape === "impotRevenu" || etape === "cipav") {
        // Trois refus motivés : l'écran dit pourquoi il s'arrête, et ce qui
        // reste vrai pour le lecteur, plutôt que d'afficher un chiffre faux.
        const texte = t[etape];
        const cible =
            etape === "micro"
                ? { fr: "eurl-ou-sasu", en: "eurl-or-sasu" }
                : etape === "impotRevenu"
                  ? { fr: "passer-a-l-impot-sur-les-societes", en: "switch-to-corporate-tax" }
                  : null;
        return (
            <Ecran key={etape} {...communsEcran} phase={PHASES[etape]} rang={rang} titre={texte.titre} aide={texte.aide}>
                <Liste items={texte.points} />
                {cible && (
                    // Le bouton porte la couleur, hors de portée de la règle
                    // globale a:hover qui repeint en rose tout lien coloré.
                    <div className="mt-5">
                        <Link href={{ pathname: "/tools/[slug]", params: { slug: cible[lang] || cible.fr } }}>
                            <Button type="primary" className="press-safe w-full">
                                {texte.lien} →
                            </Button>
                        </Link>
                    </div>
                )}
                <Recommencer onClick={recommencer} libelle={r.recommencer} />
            </Ecran>
        );
    }

    if (etape === "objectif") {
        return (
            <Question
                key="objectif"
                {...communsEcran}
                phase={PHASES.objectif}
                rang={rang}
                titre={t.objectif.titre}
                aide={t.objectif.aide}
                choix={t.objectif.choix.map((c) => ({
                    icone: c.icone,
                    titre: c.titre,
                    detail: c.detail,
                    onClick: () => {
                        setObjectif(c.cle);
                        aller(c.cle === "revendre" ? "existe" : "benefice");
                    },
                }))}
            />
        );
    }

    if (etape === "benefice") {
        return (
            <Ecran
                key="benefice"
                {...communsEcran}
                phase={PHASES.benefice}
                rang={rang}
                titre={t.benefice.titre}
                aide={t.benefice.aide}
            >
                <Curseur
                    libelle={t.benefice.libelle}
                    valeur={benefice}
                    poser={setBenefice}
                    minimum={5000}
                    maximum={300000}
                    saisie
                    pas={1000}
                    formater={sommes}
                />
                <Avancer onClick={() => aller(forme === "eurl" ? "activite" : "resultat")}>
                    {t.benefice.suivant}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "activite") {
        return (
            <Question
                key="activite"
                {...communsEcran}
                phase={PHASES.activite}
                rang={rang}
                titre={t.activite.titre}
                aide={t.activite.aide}
                choix={t.activite.choix.map((c) => ({
                    icone: c.icone,
                    titre: c.titre,
                    detail: c.detail,
                    onClick: () => {
                        if (c.cle === "cipav") {
                            aller("cipav");
                            return;
                        }
                        setActivite(c.cle);
                        aller("remuneration");
                    },
                }))}
            />
        );
    }

    if (etape === "remuneration") {
        return (
            <Ecran
                key="remuneration"
                {...communsEcran}
                phase={PHASES.remuneration}
                rang={rang}
                titre={t.remuneration.titre}
                aide={t.remuneration.aide}
            >
                <Curseur
                    libelle={t.remuneration.libelle}
                    aide={remuneration === 0 ? t.remuneration.aucune : undefined}
                    valeur={remuneration}
                    poser={setRemuneration}
                    minimum={0}
                    maximum={150000}
                    saisie
                    pas={1000}
                    formater={sommes}
                />
                <Avancer onClick={() => aller("capital")}>{t.remuneration.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "capital") {
        return (
            <Ecran
                key="capital"
                {...communsEcran}
                phase={PHASES.capital}
                rang={rang}
                titre={t.capital.titre}
                aide={remplir(t.capital.aide, {
                    seuil: `${SEUIL_DIVIDENDES_EURL * 100}${lang === "fr" ? " %" : "%"}`,
                })}
            >
                <Curseur
                    libelle={t.capital.libelle}
                    aide={remplir(t.capital.seuil, { montant: sommes(capital * SEUIL_DIVIDENDES_EURL) })}
                    valeur={capital}
                    poser={setCapital}
                    minimum={0}
                    maximum={100000}
                    saisie
                    pas={500}
                    formater={sommes}
                />
                <Avancer onClick={() => aller("resultat")}>{t.capital.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "existe") {
        return (
            <Question
                key="existe"
                {...communsEcran}
                phase={PHASES.existe}
                rang={rang}
                titre={t.existe.titre}
                aide={t.existe.aide}
                choix={t.existe.choix.map((c) => ({
                    icone: c.icone,
                    titre: c.titre,
                    detail: c.detail,
                    onClick: () => {
                        setExiste(c.cle);
                        aller("acquisition");
                    },
                }))}
            />
        );
    }

    if (etape === "acquisition") {
        const deja = existe === "deja";
        return (
            <Ecran
                key="acquisition"
                {...communsEcran}
                phase={PHASES.acquisition}
                rang={rang}
                titre={deja ? t.acquisition.titreDeja : t.acquisition.titreCreer}
                aide={deja ? t.acquisition.aideDeja : t.acquisition.aideCreer}
            >
                <Curseur
                    libelle={deja ? t.acquisition.libelleDeja : t.acquisition.libelleCreer}
                    valeur={acquisition}
                    poser={setAcquisition}
                    minimum={0}
                    maximum={100000}
                    saisie
                    pas={500}
                    formater={sommes}
                />
                <Avancer onClick={() => aller(deja ? "valeurApport" : "prix")}>{t.acquisition.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "valeurApport") {
        return (
            <Ecran
                key="valeurApport"
                {...communsEcran}
                phase={PHASES.valeurApport}
                rang={rang}
                titre={t.valeurApport.titre}
                aide={t.valeurApport.aide}
            >
                <Curseur
                    libelle={t.valeurApport.libelle}
                    valeur={valeurApport}
                    poser={setValeurApport}
                    minimum={0}
                    maximum={2000000}
                    saisie
                    pas={10000}
                    formater={sommes}
                />
                <Avancer onClick={() => aller("prix")}>{t.valeurApport.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "prix") {
        return (
            <Ecran key="prix" {...communsEcran} phase={PHASES.prix} rang={rang} titre={t.prix.titre} aide={t.prix.aide}>
                <Curseur
                    libelle={t.prix.libelle}
                    valeur={prix}
                    poser={setPrix}
                    minimum={10000}
                    maximum={3000000}
                    saisie
                    pas={10000}
                    formater={sommes}
                />
                <Avancer onClick={() => aller("delai")}>{t.prix.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "delai") {
        const deja = existe === "deja";
        return (
            <Question
                key="delai"
                {...communsEcran}
                phase={PHASES.delai}
                rang={rang}
                titre={deja ? t.delai.titreDeja : t.delai.titreCreer}
                aide={deja ? t.delai.aideDeja : t.delai.aideCreer}
                choix={(deja ? t.delai.choixDeja : t.delai.choixCreer).map((c) => ({
                    icone: c.icone,
                    titre: c.titre,
                    detail: c.detail,
                    onClick: () => {
                        setDelai(c.cle);
                        aller("resultat");
                    },
                }))}
            />
        );
    }

    const c = r.colonnes;
    const eurl = forme === "eurl";
    const encadres = [];

    let titre;
    let details;
    let sans;
    let avec;
    let hypotheses;

    if (objectif === "revendre") {
        const v = calcul;
        const apport = existe === "deja";
        const economie = v.gain;
        const report = v.avec.impotReporte;
        titre =
            economie >= ECART_NEGLIGEABLE
                ? report >= 1
                    ? remplir(r.revendre.economieReport, { economie: sommes(economie), report: sommes(report) })
                    : remplir(r.revendre.economie, { economie: sommes(economie) })
                : report >= ECART_NEGLIGEABLE
                  ? remplir(r.revendre.report, { report: sommes(report) })
                  : r.revendre.rien;
        details = [
            remplir(r.revendre.detailSans, { sans: sommes(v.sans.impot), plusValue: sommes(v.plusValue) }),
            v.avec.plusValue < 1
                ? r.revendre.detailZero
                : remplir(delai === "moins2" ? r.revendre.detailCourt : r.revendre.detailDouze, {
                      avec: sommes(v.avec.impot),
                  }),
        ].join(" ");
        sans = {
            lignes: [
                [c.prix, prix],
                [c.impotVente, v.sans.impot, true, x.impotVente],
            ],
            total: v.sans.net,
            explication: x.resteSans,
            libelle: c.surCompte,
        };
        avec = {
            lignes: [
                [c.prix, prix],
                [delai === "moins2" ? c.impotHoldingCourt : c.impotHoldingVente, v.avec.impot, true, x.impotHoldingVente],
            ],
            // L'impôt reporté n'est pas payé le jour de la vente : il a son
            // encadré plus bas, et le total ne le retire pas.
            total: v.avec.net,
            explication: x.dansHolding,
            libelle: c.dansHolding,
        };

        if (apport && v.avec.plusValueReportee >= 1) {
            encadres.push(
                <Encadre key="report" icone="safe-square-linear" titre={r.report.titre}>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                        {remplir(r.report.texte, {
                            plusValue: sommes(v.avec.plusValueReportee),
                            impot: sommes(report),
                        })}
                    </p>
                </Encadre>,
            );
        }
        if (apport && delai !== "plus3" && report >= 1) {
            encadres.push(
                <Encadre key="remploi" icone="danger-triangle-linear" titre={r.remploi.titre}>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                        {remplir(r.remploi.texte, {
                            quota: sommes(prix * APPORT_CESSION.quota),
                            report: sommes(report),
                        })}
                    </p>
                    <Liste items={r.remploi.points} />
                </Encadre>,
            );
        }
        encadres.push(
            <Encadre key="sortie" icone="wallet-money-linear" titre={r.sortie.titreRevendre}>
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                    {remplir(apport && report >= 1 ? r.sortie.texteReport : r.sortie.texte, {
                        via: sommes(sortirDeLaHolding(v.avec.net)),
                        sans: sommes(v.sans.net),
                        report: sommes(report),
                    })}
                </p>
            </Encadre>,
        );
        hypotheses = r.hypotheses.vente;
    } else {
        const v = calcul;
        const fourchette = eurl && v.cotisationsReintegrees >= 1;
        const colonneSans = [
            [c.dividendes, v.dividendes, false, x.dividendes],
            [c.impotRevenu, v.sans.impot, true, x.impotRevenu],
            // En EURL, les 18,6 % ne portent que sur les dividendes jusqu'à
            // 10 % du capital, et les cotisations sur le reste : chaque ligne
            // dit sa base.
            eurl
                ? [remplir(c.prelevementsSur, { base: sommes(v.sans.baseSociale) }), v.sans.social, true, x.prelevements]
                : [c.prelevements, v.sans.social, true, x.prelevements],
            ...(eurl
                ? [[remplir(c.cotisations, { base: sommes(v.sans.baseCotisations) }), v.sans.cotisations, true, x.cotisations]]
                : []),
        ];
        sans = { lignes: colonneSans, total: v.sans.net, explication: x.surCompte, libelle: c.surCompte };

        if (objectif === "vivre") {
            const { ecart, ecartPrudent } = v.vivre;
            titre =
                fourchette && ecart >= 1
                    ? remplir(r.vivre.fourchette, { gain: sommes(ecart), perte: sommes(-ecartPrudent) })
                    : remplir(r.vivre.perte, { ecart: sommes(Math.max(0, -ecart)) });
            details =
                fourchette && ecart >= 1
                    ? remplir(r.vivre.detailEurl, {
                          via: sommes(v.vivre.net),
                          cotisations: sommes(v.cotisationsReintegrees),
                          viaPrudent: sommes(v.vivre.net - v.cotisationsReintegrees),
                      })
                    : remplir(r.vivre.detail, { sans: sommes(v.sans.net), via: sommes(v.vivre.net) });
            avec = {
                lignes: [
                    [c.dividendes, v.dividendes, false, x.dividendes],
                    [c.impotHolding, v.avec.impot, true, x.impotHolding],
                    [c.reverse, v.avec.net - v.vivre.net, true, x.reverse],
                ],
                total: v.vivre.net,
                explication: x.via,
                libelle: c.surCompte,
            };
        } else {
            titre =
                v.gain < ECART_NEGLIGEABLE
                    ? r.reinvestir.rien
                    : fourchette
                      ? remplir(r.reinvestir.fourchette, { prudent: sommes(v.gainPrudent), gain: sommes(v.gain) })
                      : remplir(r.reinvestir.gain, { gain: sommes(v.gain) });
            details = remplir(fourchette ? r.reinvestir.detailEurl : r.reinvestir.detail, {
                dividendes: sommes(v.dividendes),
            });
            avec = {
                lignes: [
                    [c.dividendes, v.dividendes, false, x.dividendes],
                    [c.impotHolding, v.avec.impot, true, x.impotHolding],
                ],
                total: v.avec.net,
                explication: x.dansHolding,
                libelle: c.dansHolding,
            };
            encadres.push(
                <Encadre key="sortie" icone="wallet-money-linear" titre={r.sortie.titreReinvestir}>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                        {fourchette && v.vivre.ecart >= 1
                            ? remplir(r.sortie.texteEurl, {
                                  via: sommes(v.vivre.net),
                                  sans: sommes(v.sans.net),
                                  viaPrudent: sommes(v.vivre.net - v.cotisationsReintegrees),
                              })
                            : remplir(r.sortie.texte, { via: sommes(v.vivre.net), sans: sommes(v.sans.net) })}
                    </p>
                </Encadre>,
            );
            encadres.push(
                <Encadre key="integration" icone="layers-linear" titre={r.reinvestir.integrationTitre}>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                        {remplir(r.reinvestir.integration, {
                            economie: sommes(integrationFiscale({ benefice, dividendes: v.dividendes }).economie),
                        })}
                    </p>
                </Encadre>,
            );
        }
        if (fourchette) avec.note = remplir(c.prudent, { cotisations: sommes(v.cotisationsReintegrees) });
        hypotheses = eurl ? [r.hypotheses.eurl, ...r.hypotheses.dividendes] : r.hypotheses.dividendes;
    }

    return (
        <Resultat onRetour={retour} retour={t.retour} key="resultat" titre={r.titre} phases={t.phases} {...communs}>
            <div className="mt-2 font-bold text-2xl leading-tight dark:text-white">{titre}</div>
            <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">{details}</p>

            {/* LE MÊME ARGENT, DEUX CHEMINS. La colonne de gauche est ce que
                le lecteur fait aujourd'hui ; celle de droite, ce que la holding
                change, jusqu'à dire où l'argent se trouve à la fin. */}
            <IndiceExplication lang={lang} />
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <Colonne
                    titre={c.sans}
                    lignes={sans.lignes}
                    total={sans.total}
                    libelleTotal={sans.libelle}
                    explicationTotal={sans.explication}
                    lang={lang}
                    reduit={reduit}
                    ouverte={ouverte}
                    basculer={basculer}
                />
                <Colonne
                    titre={c.avec}
                    lignes={avec.lignes}
                    total={avec.total}
                    libelleTotal={avec.libelle}
                    explicationTotal={avec.explication}
                    lang={lang}
                    reduit={reduit}
                    ouverte={ouverte}
                    basculer={basculer}
                    accent
                />
            </div>
            {avec.note && (
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-2 mb-0">{avec.note}</p>
            )}

            {encadres}

            {eurl && (
                <Encadre icone="user-id-linear" titre={r.gerant.titre}>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">{r.gerant.texte}</p>
                </Encadre>
            )}

            <Encadre icone="bill-list-linear" titre={r.couts.titre}>
                <Liste items={r.couts.items} />
            </Encadre>

            <Encadre icone="shield-warning-linear" titre={r.protection.titre}>
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">{eurl ? r.protection.texteEurl : r.protection.texte}</p>
            </Encadre>

            <div className="mt-6">
                <div className="font-semibold dark:text-white">{r.hypotheses.titre}</div>
                <Liste items={hypotheses} />
            </div>

            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-6 mb-0 leading-relaxed">{r.avertissement}</p>

            <Recommencer onClick={recommencer} libelle={r.recommencer} />
        </Resultat>
    );
}
